import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  getOperationsLogs,
  getOperationsSpeechCall,
  getOperationsSpeechCalls,
} from '../services/adminApi';

const RANGE_OPTIONS = [
  ['1h', 'Last hour'],
  ['24h', 'Last 24 hours'],
  ['7d', 'Last 7 days'],
  ['30d', 'Last 30 days'],
];
const STATUS_OPTIONS = ['all', 'processing', 'completed', 'failed', 'deferred'];
const LEVEL_OPTIONS = ['all', 'debug', 'info', 'warn', 'error'];
const SUMMARY_OPTIONS = [
  ['completed', 'Completed'],
  ['failed', 'Failed'],
  ['processing', 'Processing'],
  ['deferred', 'Deferred'],
];

function displayDate(value) {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleString();
}

function displayDuration(value) {
  if (!Number.isFinite(value)) return 'Not available';
  return value >= 1000 ? `${(value / 1000).toFixed(2)} s` : `${value.toFixed(2)} ms`;
}

function titleCase(value) {
  return String(value || 'unknown').replaceAll('_', ' ');
}

function statusColor(status) {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'deferred') return 'warning';
  if (status === 'processing') return 'primary';
  return 'default';
}

function FilterSelect({ label, onChange, options, value }) {
  return (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} onChange={(event) => onChange(event.target.value)} value={value}>
        {options.map(([option, optionLabel]) => (
          <MenuItem key={option} value={option}>{optionLabel}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function Pager({ canGoBack, nextPageToken, onBack, onNext }) {
  return (
    <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
      <Button disabled={!canGoBack} onClick={onBack} variant="outlined">Previous</Button>
      <Button disabled={!nextPageToken} onClick={onNext} variant="outlined">Next</Button>
    </Stack>
  );
}

function CallDetails({ call, onClose }) {
  return (
    <Dialog fullWidth maxWidth="md" onClose={onClose} open={Boolean(call)}>
      <DialogTitle>Laptop Function Call</DialogTitle>
      <DialogContent dividers>
        {call && (
          <Stack spacing={3}>
            <Box
              display="grid"
              gap={2}
              gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }}
            >
              <Box><Typography color="text.secondary" variant="caption">Call ID</Typography><Typography sx={{ overflowWrap: 'anywhere' }}>{call.id}</Typography></Box>
              <Box><Typography color="text.secondary" variant="caption">Status</Typography><Box><Chip color={statusColor(call.status)} label={titleCase(call.status)} size="small" /></Box></Box>
              <Box><Typography color="text.secondary" variant="caption">Source</Typography><Typography>{titleCase(call.source)}</Typography></Box>
              <Box><Typography color="text.secondary" variant="caption">Characters</Typography><Typography>{call.characterCount ?? 'Not available'}</Typography></Box>
              <Box><Typography color="text.secondary" variant="caption">Started</Typography><Typography>{displayDate(call.startedAt)}</Typography></Box>
              <Box><Typography color="text.secondary" variant="caption">Finished</Typography><Typography>{displayDate(call.finishedAt)}</Typography></Box>
              <Box><Typography color="text.secondary" variant="caption">Queue time</Typography><Typography>{displayDuration(call.queueMs)}</Typography></Box>
              <Box><Typography color="text.secondary" variant="caption">Generation time</Typography><Typography>{displayDuration(call.generationMs)}</Typography></Box>
              <Box><Typography color="text.secondary" variant="caption">Total time</Typography><Typography>{displayDuration(call.totalMs)}</Typography></Box>
              <Box><Typography color="text.secondary" variant="caption">Failure code</Typography><Typography>{call.failureCode || 'Not available'}</Typography></Box>
            </Box>
            <Box>
              <Typography mb={1} variant="h6">Lifecycle Logs</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead><TableRow><TableCell>Time</TableCell><TableCell>Level</TableCell><TableCell>Event</TableCell><TableCell>Duration</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(call.events || []).map((event, index) => (
                      <TableRow key={`${event.timestamp}-${event.event}-${index}`}>
                        <TableCell>{displayDate(event.timestamp)}</TableCell>
                        <TableCell>{event.level}</TableCell>
                        <TableCell sx={{ overflowWrap: 'anywhere' }}>{event.event}</TableCell>
                        <TableCell>{displayDuration(event.durationMs)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

export function SpeechCallsView() {
  const [range, setRange] = useState('24h');
  const [status, setStatus] = useState('all');
  const [pages, setPages] = useState([null]);
  const [data, setData] = useState({
    items: [],
    total: 0,
    summary: { completed: 0, deferred: 0, failed: 0, processing: 0 },
    nextPageToken: null,
  });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const pageToken = pages.at(-1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getOperationsSpeechCalls({ range, status, pageToken });
      setData(response.data);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Laptop function calls are unavailable');
    } finally {
      setLoading(false);
    }
  }, [pageToken, range, status]);

  useEffect(() => { load(); }, [load]);

  async function openCall(id) {
    try {
      const response = await getOperationsSpeechCall(id);
      setSelected(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Laptop function call is unavailable');
    }
  }

  function resetPages(setter) {
    return (value) => {
      setter(value);
      setPages([null]);
    };
  }

  return (
    <Stack spacing={2}>
      <Stack alignItems={{ xs: 'stretch', sm: 'center' }} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
        <Box>
          <Typography variant="h5">Laptop Function Calls</Typography>
          <Typography color="text.secondary" variant="body2">{data.total} matching calls in this time range</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <FilterSelect label="Time range" onChange={resetPages(setRange)} options={RANGE_OPTIONS} value={range} />
          <FilterSelect label="Status" onChange={resetPages(setStatus)} options={STATUS_OPTIONS.map((value) => [value, titleCase(value)])} value={status} />
          <Button onClick={load} variant="outlined">Refresh</Button>
        </Stack>
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={3}>
        {SUMMARY_OPTIONS.map(([key, label]) => (
          <Box key={key} sx={{ minWidth: 90 }}>
            <Typography color="text.secondary" variant="caption">{label}</Typography>
            <Typography variant="h6">{data.summary?.[key] || 0}</Typography>
          </Box>
        ))}
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {loading ? <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead><TableRow><TableCell>Started</TableCell><TableCell>Status</TableCell><TableCell>Source</TableCell><TableCell>Characters</TableCell><TableCell>Queue</TableCell><TableCell>Generation</TableCell><TableCell>Total</TableCell></TableRow></TableHead>
            <TableBody>
              {data.items.length === 0 ? (
                <TableRow><TableCell colSpan={7}>No laptop calls were recorded in this time range.</TableCell></TableRow>
              ) : data.items.map((call) => (
                <TableRow hover key={call.id} onClick={() => openCall(call.id)} sx={{ cursor: 'pointer' }}>
                  <TableCell>{displayDate(call.startedAt)}</TableCell>
                  <TableCell><Chip color={statusColor(call.status)} label={titleCase(call.status)} size="small" /></TableCell>
                  <TableCell>{titleCase(call.source)}</TableCell>
                  <TableCell>{call.characterCount ?? 'Not available'}</TableCell>
                  <TableCell>{displayDuration(call.queueMs)}</TableCell>
                  <TableCell>{displayDuration(call.generationMs)}</TableCell>
                  <TableCell>{displayDuration(call.totalMs)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Pager
        canGoBack={pages.length > 1}
        nextPageToken={data.nextPageToken}
        onBack={() => setPages((current) => current.slice(0, -1))}
        onNext={() => setPages((current) => [...current, data.nextPageToken])}
      />
      <CallDetails call={selected} onClose={() => setSelected(null)} />
    </Stack>
  );
}

export function OperationsLogsView() {
  const [range, setRange] = useState('24h');
  const [level, setLevel] = useState('all');
  const [event, setEvent] = useState('');
  const [route, setRoute] = useState('');
  const [requestId, setRequestId] = useState('');
  const [draftEvent, setDraftEvent] = useState('');
  const [draftRoute, setDraftRoute] = useState('');
  const [draftRequestId, setDraftRequestId] = useState('');
  const [pages, setPages] = useState([null]);
  const [data, setData] = useState({ items: [], nextPageToken: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const pageToken = pages.at(-1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getOperationsLogs({ range, level, event, route, requestId, pageToken });
      setData(response.data);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Operational logs are unavailable');
    } finally {
      setLoading(false);
    }
  }, [event, level, pageToken, range, requestId, route]);

  useEffect(() => { load(); }, [load]);

  function update(setter) {
    return (value) => {
      setter(value);
      setPages([null]);
    };
  }

  function applyFilters() {
    setEvent(draftEvent);
    setRoute(draftRoute);
    setRequestId(draftRequestId);
    setPages([null]);
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h5">Operational Logs</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} flexWrap="wrap" gap={1}>
        <FilterSelect label="Time range" onChange={update(setRange)} options={RANGE_OPTIONS} value={range} />
        <FilterSelect label="Level" onChange={update(setLevel)} options={LEVEL_OPTIONS.map((value) => [value, titleCase(value)])} value={level} />
        <TextField label="Event" onChange={(e) => setDraftEvent(e.target.value)} size="small" value={draftEvent} />
        <TextField label="Route" onChange={(e) => setDraftRoute(e.target.value)} size="small" value={draftRoute} />
        <TextField label="Request ID" onChange={(e) => setDraftRequestId(e.target.value)} size="small" value={draftRequestId} />
        <Button onClick={applyFilters} variant="contained">Apply Filters</Button>
        <Button onClick={load} variant="outlined">Refresh</Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {loading ? <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead><TableRow><TableCell>Time</TableCell><TableCell>Level</TableCell><TableCell>Event</TableCell><TableCell>Route</TableCell><TableCell>Request ID</TableCell><TableCell>Duration</TableCell></TableRow></TableHead>
            <TableBody>
              {data.items.length === 0 ? (
                <TableRow><TableCell colSpan={6}>No operational logs match these filters.</TableCell></TableRow>
              ) : data.items.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{displayDate(entry.timestamp)}</TableCell>
                  <TableCell>{entry.level}</TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>{entry.event}</TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>{entry.route || 'Not available'}</TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>{entry.requestId || 'Not available'}</TableCell>
                  <TableCell>{displayDuration(entry.durationMs)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Pager
        canGoBack={pages.length > 1}
        nextPageToken={data.nextPageToken}
        onBack={() => setPages((current) => current.slice(0, -1))}
        onNext={() => setPages((current) => [...current, data.nextPageToken])}
      />
    </Stack>
  );
}
